import { useEffect, useRef, useState } from 'react';
import type { TauntItem } from '@tigrinho/server/protocol';
import { ART, CARD_ART, ITEM_ART, pawnArt } from '../lib/art';
import { CARD_TEXT, DECK_LABEL, GROUP_COLORS, TAUNT_EMOJI } from '../lib/content';
import { type Moment, momentOf } from '../lib/describe';
import { audio } from '../lib/audio';
import { prefs } from '../lib/storage';
import { useHold, useRoomCtx } from '../screens/RoomScreen';
import { BOARD_SIZE } from '@tigrinho/engine';
import { CARD_READ_MS, EVADE_MS, MST_EXPEL_MS, MST_INVADE_MS, MST_MARCH_MS, STEP_MS, walkMs } from '../net/useRoom';

/** Itens voando entre os jogadores, respingos e combos. */
export function TauntLayer() {
  const { bus, you, send } = useRoomCtx();
  const layer = useRef<HTMLDivElement>(null);
  const [revenge, setRevenge] = useState<{ from: string; item: TauntItem; at: number } | null>(null);
  const [combo, setCombo] = useState<{ target: string; count: number; at: number } | null>(null);

  useEffect(
    () =>
      bus.onTaunt((m) => {
        if (prefs.mutedTaunts() && m.target !== you) return;
        const from = document.getElementById(`chip-${m.from}`);
        const to = document.getElementById(`chip-${m.target}`);
        const box = layer.current;
        if (!from || !to || !box) return;
        const a = from.getBoundingClientRect();
        const b = to.getBoundingClientRect();
        const el = document.createElement('span');
        el.className = 'flying';
        const itemArt = ITEM_ART[m.item];
        if (itemArt) {
          const img = document.createElement('img');
          img.src = itemArt.item;
          img.alt = '';
          el.appendChild(img);
          el.classList.add('has-art');
        } else {
          el.textContent = TAUNT_EMOJI[m.item] ?? '🍅';
        }
        box.appendChild(el);
        const x0 = a.left + a.width / 2;
        const y0 = a.top + a.height / 2;
        const x1 = b.left + b.width / 2 + (Math.random() - 0.5) * 24;
        const y1 = b.top + b.height / 2 + (Math.random() - 0.5) * 12;
        const reduced = prefs.reducedEffects();
        const anim = el.animate(
          [
            { transform: `translate(${x0}px, ${y0}px) scale(.6) rotate(0deg)`, opacity: 1 },
            { transform: `translate(${(x0 + x1) / 2}px, ${Math.min(y0, y1) - 90}px) scale(1.4) rotate(200deg)`, offset: 0.5 },
            { transform: `translate(${x1}px, ${y1}px) scale(1) rotate(400deg)`, opacity: 1 },
          ],
          { duration: reduced ? 250 : 650, easing: 'cubic-bezier(.3,.7,.4,1)' },
        );
        anim.onfinish = () => {
          el.className = itemArt ? 'splat has-art' : 'splat';
          if (itemArt?.splat) (el.firstElementChild as HTMLImageElement).src = itemArt.splat;
          el.style.transform = `translate(${x1}px, ${y1}px)`;
          if (!reduced) to.animate([{ transform: 'translateX(-4px)' }, { transform: 'translateX(4px)' }, { transform: 'none' }], 180);
          setTimeout(() => el.remove(), 2600);
        };
        if (m.combo >= 3) setCombo({ target: m.target, count: m.combo, at: Date.now() });
        if (m.target === you) setRevenge({ from: m.from, item: m.item, at: Date.now() });
      }),
    [bus, you],
  );

  useEffect(() => {
    if (!revenge) return;
    const t = setTimeout(() => setRevenge(null), 3500);
    return () => clearTimeout(t);
  }, [revenge]);

  useEffect(() => {
    if (!combo) return;
    const t = setTimeout(() => setCombo(null), 1400);
    return () => clearTimeout(t);
  }, [combo]);

  return (
    <>
      <div className="taunt-layer" ref={layer} aria-hidden="true" />
      {combo && (
        <ComboBadge key={combo.at} target={combo.target} count={combo.count} />
      )}
      {revenge && (
        <button
          className="revenge-btn"
          onClick={() => {
            send({ t: 'taunt', target: revenge.from, item: revenge.item });
          }}
        >
          Devolver {TAUNT_EMOJI[revenge.item]}
        </button>
      )}
    </>
  );
}

function ComboBadge({ target, count }: { target: string; count: number }) {
  const r = document.getElementById(`chip-${target}`)?.getBoundingClientRect();
  if (!r) return null;
  return (
    <span className="combo" style={{ left: r.left + r.width / 2, top: r.bottom + 4 }} aria-hidden="true">
      x{count}!
    </span>
  );
}

// ---------------------------------------------------------------------------

/**
 * Moedas voando: de quem paga para quem recebe (aluguel, tomada, cartas) e saindo da casa
 * do Início quando alguém passa por ela. Tudo no ritmo do peão (espera ele chegar).
 */
export function MoneyFlyLayer() {
  const { bus } = useRoomCtx();
  const layer = useRef<HTMLDivElement>(null);
  useEffect(
    () =>
      bus.onEvents(({ events, room }) => {
        if (prefs.reducedEffects()) return;
        const size = room.game?.props.length ?? BOARD_SIZE;
        const arrive = walkMs(events, size);
        const flows: { from: string; to: string; amount: number; delay: number }[] = [];
        let walked = 0;
        for (const e of events) {
          if (e.type === 'moved') {
            if (e.how === 'walk') {
              // Moedas do Início saem quando o peão passa por ele.
              const toStart = (size - e.from) % size;
              const steps = (e.to - e.from + size) % size;
              const passes = events.some((x) => x.type === 'passedStart' && x.player === e.player);
              if (passes && toStart > 0 && toStart <= steps) flows.push({ from: 'square-0', to: `chip-${e.player}`, amount: 300, delay: walked + toStart * STEP_MS });
              walked += steps * STEP_MS;
            } else walked += 750;
          } else if (e.type === 'rentPaid') flows.push({ from: `chip-${e.payer}`, to: `chip-${e.owner}`, amount: e.amount, delay: arrive });
          else if (e.type === 'takeover') flows.push({ from: `chip-${e.buyer}`, to: `chip-${e.from}`, amount: e.price, delay: arrive + 300 });
          else if (e.type === 'paid' && e.to !== 'bank' && e.reason !== 'aluguel')
            flows.push({ from: `chip-${e.from}`, to: `chip-${e.to}`, amount: e.amount, delay: arrive + 200 });
        }
        flows.forEach((f, k) => setTimeout(() => fly(f.from, f.to, f.amount), f.delay + k * 120));
      }),
    [bus],
  );

  function fly(fromId: string, toId: string, amount: number): void {
    const a = document.getElementById(fromId)?.getBoundingClientRect();
    const b = document.getElementById(toId)?.getBoundingClientRect();
    const box = layer.current;
    if (!a || !b || !box) return;
    const coins = Math.min(9, 3 + Math.floor(Math.log2(Math.max(1, amount / 25))));
    for (let i = 0; i < coins; i++) {
      const el = document.createElement('img');
      el.src = ART.money;
      el.className = 'fly-coin';
      box.appendChild(el);
      const x0 = a.left + a.width * 0.5 + (Math.random() - 0.5) * 30;
      const y0 = a.top + a.height * 0.5;
      const x1 = b.left + b.width * 0.6 + (Math.random() - 0.5) * 30;
      const y1 = b.top + b.height / 2;
      const mx = (x0 + x1) / 2 + (Math.random() - 0.5) * 160;
      const my = Math.min(y0, y1) - 60 - Math.random() * 80;
      const anim = el.animate(
        [
          { transform: `translate(${x0}px, ${y0}px) scale(.6)`, opacity: 0 },
          { transform: `translate(${x0}px, ${y0}px) scale(1)`, opacity: 1, offset: 0.08 },
          { transform: `translate(${mx}px, ${my}px) scale(1.2) rotate(180deg)`, offset: 0.5 },
          { transform: `translate(${x1}px, ${y1}px) scale(.7) rotate(360deg)`, opacity: 1 },
        ],
        { duration: 900, delay: i * 70, easing: 'cubic-bezier(.4,0,.3,1)', fill: 'both' },
      );
      anim.onfinish = () => el.remove();
    }
  }

  return <div className="money-layer" ref={layer} aria-hidden="true" />;
}

// ---------------------------------------------------------------------------

/** Marcos de riqueza em unidades do motor (x 1.000 contos na tela): 2, 5 e 10 milhões. */
export const MILESTONES = [2000, 5000, 10000];

/** Avisa a tela inteira (placar e peão) que alguém está comemorando. */
export function celebrate(player: string, ms = 4500): void {
  window.dispatchEvent(new CustomEvent('tt:celebrate', { detail: { player, until: Date.now() + ms } }));
}

const DURATION: Partial<Record<Moment['kind'], number>> = {
  milestone: 3600, card: CARD_READ_MS - 150, facada: 2600, banner: 2400, bankrupt: 3000, takeover: 2800, group: 2200, hotel: 2400, destroyed: 2200 };

/** Momentos grandes (carta puxada, facada, quebra...) um de cada vez. */
export function Moments() {
  const ctx = useRoomCtx();
  const { bus, you, name, square, money, room } = ctx;
  const [queue, setQueue] = useState<Moment[]>([]);
  const [flash, setFlash] = useState<null | 'facada' | 'mortal'>(null);
  const reached = useRef<Record<string, number>>({});
  // Ao abrir a tela, o que cada um já tem não conta como conquista nova.
  if (!Object.keys(reached.current).length)
    for (const p of ctx.room.game?.players ?? []) reached.current[p.id] = MILESTONES.filter((v) => p.cash >= v).length;

  useEffect(
    () =>
      bus.onEvents(({ events, room }) => {
        const ms = events.map(momentOf).filter((m): m is Moment => m !== null);
        // Marcos de 2, 5 e 10 milhões: só comemora quando passa do marco durante a partida.
        for (const p of room.game?.players ?? []) {
          const level = MILESTONES.filter((v) => p.cash >= v).length;
          const before = reached.current[p.id];
          reached.current[p.id] = Math.max(before ?? level, level);
          if (before !== undefined && level > before) {
            ms.push({ kind: 'milestone', player: p.id, millions: MILESTONES[level - 1]! / 1000 });
            celebrate(p.id);
            audio.sfx('hotel', 0.2);
          }
        }
        // Destruições e escudos repetidos viram um só momento.
        if (ms.length) setQueue((q) => [...q, ...ms].slice(-6));
        const f = events.find((e) => e.type === 'rentPaid' && e.facada && e.payer === you);
        if (f && f.type === 'rentPaid') {
          setFlash(f.mortal ? 'mortal' : 'facada');
          navigator.vibrate?.(f.mortal ? [120, 60, 220] : 120);
          setTimeout(() => setFlash(null), 1600);
        }
        if (events.some((e) => e.type === 'turnStarted' && e.player === you)) navigator.vibrate?.(60);
      }),
    [bus, you],
  );

  const held = useHold();
  const m = held ? undefined : queue[0];
  useEffect(() => {
    if (!m) return;
    const t = setTimeout(() => setQueue((q) => q.slice(1)), prefs.reducedEffects() ? 1200 : (DURATION[m.kind] ?? 1800));
    return () => clearTimeout(t);
  }, [m]);

  const skip = () => setQueue((q) => q.slice(1));
  const art = (id: string) => pawnArt(room.seats.find((x) => x.id === id)?.pawn ?? 0);
  const whoName = (id: string) => (id === you ? 'Você' : name(id));

  return (
    <>
      {flash && <div className={`vignette vignette-${flash}`} aria-hidden="true" />}
      {m && (
        <div className={`moment moment-${m.kind}`} role="status" onClick={skip}>
          {m.kind === 'card' && <CardReveal m={m} who={whoName(m.player)} />}
          {m.kind === 'facada' && (
            <div className="burst burst-bad has-cast">
              <div className="cast">
                <img className="cast-sad" src={art(m.payer).sad} alt="" />
                <img className="cast-happy" src={art(m.owner).happy} alt="" />
              </div>
              <strong>{m.mortal ? 'Facada mortal' : 'Facada'}</strong>
              <span className="burst-amount">−{money(m.amount)}</span>
              <span>
                {whoName(m.payer)} em {square(m.square)}
              </span>
            </div>
          )}
          {m.kind === 'group' && (
            <div className="burst burst-good has-cast" style={{ '--band': GROUP_COLORS[m.group] } as React.CSSProperties}>
              <div className="cast">
                <img className="cast-happy is-solo" src={art(m.player).happy} alt="" />
              </div>
              <strong>Grupo fechado</strong>
              <span>{whoName(m.player)} cobra aluguel em dobro nesse grupo</span>
            </div>
          )}
          {m.kind === 'hotel' && (
            <div className="burst burst-good has-cast">
              <div className="cast">
                <img className="cast-happy is-solo" src={art(m.player).happy} alt="" />
              </div>
              <strong>Primeiro hotel</strong>
              <span>
                {whoName(m.player)} em {square(m.square)}
              </span>
            </div>
          )}
          {m.kind === 'bankrupt' && (
            <div className="burst burst-bad has-cast">
              <div className="cast">
                <img className="cast-sad is-solo" src={art(m.player).sad} alt="" />
              </div>
              <strong>Faliu</strong>
              <span>{whoName(m.player)} está fora da partida. As cidades voltaram para o banco.</span>
            </div>
          )}
          {m.kind === 'takeover' && (
            <div className="burst burst-bad has-cast">
              <div className="cast">
                <img className="cast-sad" src={art(m.from).sad} alt="" />
                <img className="cast-happy" src={art(m.buyer).happy} alt="" />
              </div>
              <strong>Tomou!</strong>
              <span className="burst-amount">{money(m.price)}</span>
              <span>
                {whoName(m.buyer)} tomou {square(m.square)} de {whoName(m.from)}
              </span>
            </div>
          )}
          {m.kind === 'destroyed' && (
            <div className="burst burst-bad has-cast">
              <div className="cast">
                <img className="cast-sad is-solo" src={art(m.owner).sad} alt="" />
              </div>
              <strong>Casa no chão</strong>
              <span>
                {m.by ? `${whoName(m.by)} destruiu` : 'Caiu'} uma casa de {whoName(m.owner)} em {square(m.square)}
              </span>
            </div>
          )}
          {m.kind === 'shield' && (
            <div className="burst burst-good">
              <strong>Seguro Anti-Zoeira</strong>
              <span>{whoName(m.target)} bloqueou o ataque</span>
            </div>
          )}
          {m.kind === 'milestone' && (
            <div className="burst burst-hot has-cast is-milestone">
              <div className="cast">
                <img className="cast-happy is-solo" src={art(m.player).happy} alt="" />
              </div>
              <strong>{m.millions} milhões!</strong>
              <span>
                {whoName(m.player)} chegou a {m.millions} milhões de contos no bolso
              </span>
              <span className="coin-rain" aria-hidden="true">
                {Array.from({ length: 14 }, (_, i) => (
                  <img key={i} src={ART.money} alt="" style={{ left: `${(i * 23) % 100}%`, animationDelay: `${(i % 7) * 0.12}s` }} />
                ))}
              </span>
            </div>
          )}
          {m.kind === 'banner' && (
            <div className={`burst burst-${m.tone === 'bad' ? 'bad' : 'hot'}`}>
              <strong>{m.title}</strong>
              <span>{m.line}</span>
            </div>
          )}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

/** Centro de uma casa do tabuleiro na tela. */
function cellCenter(i: number): { x: number; y: number } | null {
  const r = document.getElementById(`cell-${i}`)?.getBoundingClientRect();
  return r ? { x: r.left + r.width / 2, y: r.top + r.height / 2 } : null;
}

const EASE_OUT = 'cubic-bezier(0.22, 1, 0.36, 1)';
const EASE_IN = 'cubic-bezier(0.55, 0, 0.75, 0.2)';

/**
 * Invasão do MST: a turma entra pelo canto da tela marchando (curvas suaves, nada linear),
 * finca a bandeira na cidade parada e espera o dono. Se o dono chamar o fazendeiro, ele chega
 * com a liminar e o cachorro bravo e a turma sai correndo; se não, ela derruba um nível e vai embora.
 */
export function MstLayer() {
  const { bus } = useRoomCtx();
  const layer = useRef<HTMLDivElement>(null);
  const camps = useRef<Record<number, HTMLElement>>({});

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    const reduced = () => prefs.reducedEffects();

    const march = (sq: number) => {
      const box = layer.current;
      const c = cellCenter(sq);
      if (!box || !c) return;
      camps.current[sq]?.remove();
      const el = document.createElement('div');
      el.className = 'mst-group';
      el.innerHTML = `<img src="${ART.mst}" alt="" /><span class="mst-tag">MST</span>`;
      box.appendChild(el);
      camps.current[sq] = el;
      const x0 = -220;
      const y0 = window.innerHeight + 40;
      const mx = c.x * 0.45;
      const my = Math.min(y0, c.y) + (y0 - c.y) * 0.35;
      el.animate(
        [
          { transform: `translate(${x0}px, ${y0}px) scale(.7)`, opacity: 0, offset: 0 },
          { transform: `translate(${x0 + 60}px, ${y0 - 30}px) scale(.8)`, opacity: 1, offset: 0.08 },
          { transform: `translate(${mx}px, ${my}px) scale(.95)`, offset: 0.55, easing: EASE_OUT },
          { transform: `translate(${c.x}px, ${c.y}px) scale(1)`, offset: 1 },
        ],
        { duration: reduced() ? 300 : MST_MARCH_MS - 250, easing: 'cubic-bezier(0.45, 0.05, 0.25, 1)', fill: 'forwards' },
      );
      if (!reduced()) {
        const img = el.firstElementChild as HTMLElement;
        img.animate([{ translate: '0 0', rotate: '-3deg' }, { translate: '0 -8%', rotate: '3deg' }, { translate: '0 0', rotate: '-3deg' }], {
          duration: 380,
          iterations: Math.ceil(MST_MARCH_MS / 380),
          easing: 'ease-in-out',
        });
      }
      audio.sfx('tension');
    };

    const leave = (sq: number, run: boolean) => {
      const el = camps.current[sq];
      if (!el) return;
      delete camps.current[sq];
      const c = cellCenter(sq) ?? { x: 0, y: 0 };
      const img = el.firstElementChild as HTMLElement;
      img.style.transform = run ? 'scaleX(-1)' : '';
      const anim = el.animate(
        [
          { transform: `translate(${c.x}px, ${c.y}px) scale(1)`, opacity: 1 },
          { transform: `translate(${run ? -260 : window.innerWidth + 260}px, ${c.y + (run ? 120 : -40)}px) scale(${run ? 0.8 : 0.9})`, opacity: 0.9 },
        ],
        { duration: run ? 900 : 1100, easing: EASE_IN, fill: 'forwards' },
      );
      anim.onfinish = () => el.remove();
    };

    const invade = (sq: number) => {
      const el = camps.current[sq];
      const cell = document.getElementById(`cell-${sq}`);
      if (cell && !reduced()) cell.animate([{ filter: 'none' }, { filter: 'brightness(1.4) saturate(1.4)' }, { filter: 'none' }], { duration: 500, iterations: 2 });
      if (el && !reduced()) {
        (el.firstElementChild as HTMLElement).animate([{ translate: '0 0' }, { translate: '0 -18%' }, { translate: '0 0' }], {
          duration: 260,
          iterations: 3,
          easing: EASE_OUT,
        });
      }
      pop(sq, '−1 nível', 'is-bad');
      audio.sfx('boom');
      timers.push(setTimeout(() => leave(sq, false), MST_INVADE_MS - 500));
    };

    const expel = (sq: number) => {
      const box = layer.current;
      const c = cellCenter(sq);
      if (!box || !c) return leave(sq, true);
      const f = document.createElement('div');
      f.className = 'farmer';
      f.innerHTML = `<img src="${ART.farmer}" alt="" /><span class="farmer-bark">AU AU!</span><span class="farmer-paper">LIMINAR</span>`;
      box.appendChild(f);
      const tx = c.x + 90;
      f.animate(
        [
          { transform: `translate(${window.innerWidth + 200}px, ${c.y + 30}px) scale(.8)`, opacity: 0 },
          { transform: `translate(${tx}px, ${c.y}px) scale(1.05)`, opacity: 1, offset: 0.7 },
          { transform: `translate(${tx}px, ${c.y}px) scale(1)`, opacity: 1 },
        ],
        { duration: reduced() ? 200 : 800, easing: EASE_OUT, fill: 'forwards' },
      );
      audio.sfx('shield');
      timers.push(setTimeout(() => leave(sq, true), reduced() ? 250 : 900));
      timers.push(
        setTimeout(() => {
          const a = f.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 400, fill: 'forwards' });
          a.onfinish = () => f.remove();
        }, MST_EXPEL_MS - 400),
      );
    };

    const pop = (sq: number, text: string, cls: string) => {
      const box = layer.current;
      const c = cellCenter(sq);
      if (!box || !c) return;
      const el = document.createElement('span');
      el.className = `mst-pop ${cls}`;
      el.textContent = text;
      el.style.left = `${c.x}px`;
      el.style.top = `${c.y}px`;
      box.appendChild(el);
      timers.push(setTimeout(() => el.remove(), 1600));
    };

    const off = bus.onEvents(({ events }) => {
      let t = 0;
      for (const e of events) {
        if (e.type === 'mstArrived') {
          const sq = e.square;
          timers.push(setTimeout(() => march(sq), t));
          t += MST_MARCH_MS;
        } else if (e.type === 'mstInvaded') {
          const sq = e.square;
          timers.push(setTimeout(() => invade(sq), t));
          t += MST_INVADE_MS;
        } else if (e.type === 'mstExpelled') {
          const sq = e.square;
          timers.push(setTimeout(() => expel(sq), t));
          t += MST_EXPEL_MS;
        } else if (e.type === 'farmProtected') {
          const sq = e.square;
          timers.push(setTimeout(() => pop(sq, 'Protegida', 'is-good'), t + 300));
        }
      }
    });
    return () => {
      off();
      timers.forEach(clearTimeout);
    };
  }, [bus]);

  return <div className="mst-layer" ref={layer} aria-hidden="true" />;
}

/** Sonegação: dois dados rolando no meio da tela; 8 ou mais escapa. */
export function EvadeDice() {
  const { bus } = useRoomCtx();
  const [roll, setRoll] = useState<{ dice: number[]; total: number; success: boolean; at: number } | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(
    () =>
      bus.onEvents(({ events }) => {
        const e = events.find((x) => x.type === 'taxEvasion');
        if (e && e.type === 'taxEvasion') setRoll({ dice: e.dice, total: e.total, success: e.success, at: Date.now() });
      }),
    [bus],
  );
  useEffect(() => {
    if (!roll) return;
    const spin = setInterval(() => setTick((v) => v + 1), 80);
    const end = setTimeout(() => setRoll(null), EVADE_MS + 900);
    return () => {
      clearInterval(spin);
      clearTimeout(end);
    };
  }, [roll]);
  if (!roll) return null;
  const done = Date.now() - roll.at > EVADE_MS * 0.6;
  const faces = done ? roll.dice : [1 + (tick % 6), 1 + ((tick * 5 + 2) % 6)];
  return (
    <div className="evade" role="status">
      <p className="evade-title">Sonegando… precisa de 8 ou mais</p>
      <div className="evade-dice">
        {faces.map((f, i) => (
          <span key={i} className={`evade-die ${done ? 'is-done' : ''}`}>
            {f}
          </span>
        ))}
      </div>
      {done && <p className={`evade-verdict ${roll.success ? 'is-ok' : 'is-bad'}`}>{roll.total} · {roll.success ? 'Escapou da Receita!' : 'Pego!'}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * "Sorteando quem começa…": os peões giram numa roleta e, depois, a ordem sorteada aparece
 * de 1º a último. Todos começam com o mesmo dinheiro; só a ordem é sorte.
 */
export function OrderDraw() {
  const { ordering, room, you, name } = useRoomCtx();
  const [tick, setTick] = useState(0);
  const g = room.game;
  const spinning = ordering !== null && Date.now() - ordering < 1500;
  useEffect(() => {
    if (ordering === null) return;
    const t = setInterval(() => setTick((v) => v + 1), 110);
    return () => clearInterval(t);
  }, [ordering]);
  if (ordering === null || !g) return null;
  const order = g.players;
  const pawnOf = (id: string) => pawnArt(room.seats.find((s) => s.id === id)?.pawn ?? 0);
  const spin = order[tick % order.length]!;
  return (
    <div className="order-draw" role="status">
      <div className="order-box">
        <p className="order-title">{spinning ? 'Sorteando quem começa…' : 'Ordem da partida'}</p>
        {spinning ? (
          <div className="order-spin" aria-hidden="true">
            <img key={tick} src={pawnOf(spin.id).idle} alt="" />
          </div>
        ) : (
          <ol className="order-list">
            {order.map((p, i) => (
              <li key={p.id} className={p.id === you ? 'is-me' : ''} style={{ animationDelay: `${i * 0.22}s` }}>
                <b>{i + 1}º</b>
                <img src={pawnOf(p.id).idle} alt="" />
                <span>{p.id === you ? 'Você' : name(p.id)}</span>
              </li>
            ))}
          </ol>
        )}
        <p className="order-note">Todos começam com 2 000 000 de contos</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Carta "F total ou Legaaaal": sai do baralho girando como carta arremessada na mesa, cai com
 * um tranco e leva um carimbo com o veredito (LEGAAAAL!, F TOTAL ou CAOS). Com o trevo da
 * Sorte de Malandro, um selinho avisa que a sorte ajudou.
 */
function CardReveal({ m, who }: { m: Extract<Moment, { kind: 'card' }>; who: string }) {
  const deck = CARD_TEXT[m.card]?.deck ?? 'sorte';
  const stamp = deck === 'sorte' ? 'LEGAAAAL!' : deck === 'reves' ? 'F TOTAL' : 'CAOS';
  return (
    <div className={`card-toss deck-${deck}`}>
      <div className={`card card-${deck}`}>
        <img className="card-art" src={CARD_ART[deck]} alt="" />
        <p className="card-deck">{DECK_LABEL[deck]}</p>
        <h3>{CARD_TEXT[m.card]?.title ?? m.card}</h3>
        <p>{CARD_TEXT[m.card]?.text}</p>
        <p className="card-who">{who} puxou</p>
        <span className="card-stamp" aria-hidden="true">
          {stamp}
        </span>
        {m.clover && <span className="card-clover">🍀 Sorte de Malandro</span>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

/** Cenário que acompanha a tensão: disco voador e chuva de boleto no Caos final. */
export function TensionScene({ level }: { level: 1 | 2 | 3 | 4 }) {
  if (prefs.reducedEffects() || level < 4) return null;
  return (
    <div className="tension-scene" aria-hidden="true">
      <img className="scene-ufo" src={ART.ufo} alt="" />
          {Array.from({ length: 12 }, (_, i) => (
            <img
              key={i}
              className="scene-boleto"
              src={ART.boleto}
              alt=""
              style={
                {
                  left: `${(i * 37) % 100}%`,
                  animationDelay: `${(i * 1.7) % 9}s`,
                  animationDuration: `${7 + ((i * 3) % 5)}s`,
                  '--spin': `${i % 2 ? 1 : -1}`,
                } as React.CSSProperties
              }
            />
          ))}
    </div>
  );
}
